import { BadgeColor, BadgeProps } from "@beydesign/storybook"
import dayjs from "dayjs"
import { FirebaseError } from "firebase/app"
import { DefaultAgentCreatorId } from "../constants/common"
import { useAppSelector } from "../redux"
import {
  Agent,
  AgentScreen,
  Avatar,
  CheckoutData,
  Conversation,
  ConversationStatus,
  Feedback,
  FeedbackEntity,
  ModuleName,
  Screen,
  StripeProducts,
  SubscriptionPlan,
  User,
  VideoScreen,
} from "../types"
import { UsageDetails } from "../types/Usage"

export const isLocalDev = process.env.NODE_ENV === "development"

export const isDev = isLocalDev || window.location.href.includes("dev")

export const isStaging = window.location.href.includes("staging")

export const isProduction = !isLocalDev && !isDev && !isStaging

export const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const calculatePrice = (checkoutData: CheckoutData): number => {
  const normalAmount =
    Math.round(checkoutData.wordsCount * 0.01 * checkoutData.quantity * 100) /
    100
  return normalAmount < 0.5 ? 0.5 : normalAmount
}

export const getAvailableCredits = (
  usageDetails: UsageDetails | null,
): number => {
  if (!usageDetails) return 0
  const usedCredits = (usageDetails.current_usage / 60) * 100
  const totalCredits = (usageDetails.usage_limit / 60) * 100
  const remainingCredits = totalCredits - usedCredits
  return remainingCredits
}

type GetNavigationPaths = (moduleName: ModuleName) => {
  back: string
  next: string
  initial: string
}
export const getNavigationPaths: GetNavigationPaths = (moduleName) => {
  switch (moduleName) {
    case ModuleName.videos:
      return {
        back: `/videos`,
        next: `/${Screen.videos}/${VideoScreen.createVideoMessage}`,
        initial: "/videos",
      }
    case ModuleName.agents:
      return {
        back: `/${Screen.myAgents}`,
        next: `/${Screen.myAgents}/${AgentScreen.agentsInfo}`,
        initial: `/${Screen.myAgents}`,
      }
    case ModuleName.home:
      return {
        back: `/`,
        next: `/${Screen.myAgents}/${AgentScreen.agentsInfo}`,
        initial: `/`,
      }
  }
}

export const getFirebaseErrorMessage = (error: FirebaseError): string => {
  switch (error.code) {
    case "auth/id-token-expired":
      return "Your session has expired. Please log in again."
    case "auth/id-token-revoked":
      return "Your session has been revoked. Please log in again."
    case "auth/email-already-in-use":
      return "Email already in use. Please use login instead."
    case "auth/invalid-credential":
      return "Invalid credentials. Please check your email and password."
    case "auth/invalid-password":
      return "Invalid password. Please check your password and try again."
    case "auth/too-many-requests":
      return "You have made too many requests. Please try again later."
    case "auth/invalid-verification-code":
      return "Invalid verification code. Please check your code and try again."
    case "auth/user-disabled":
      return "Your account has been disabled. Please contact support."
    case "auth/user-not-found":
      return "User not found. Please check your email and try again."
    case "auth/invalid-action-code":
      return "Your reset password link is invalid. Please request a new one."
    default:
      return "An error occurred. Please try again."
  }
}

export const getEntitiesWithFeedback = (
  feedbacks: Feedback[],
): Set<FeedbackEntity> => {
  const entities = feedbacks
    .map((feedback) => feedback.entityType)
    .filter((e) => e != null)
  return new Set(entities)
}

/**
 * Generate a URL for the agent chat
 * @param agentId - The ID of the agent to embed
 * @returns URL for the agent chat
 */
export const getAgentChatUrl = (agentId: string) => {
  const baseUrl = isLocalDev
    ? "http://localhost:5173"
    : isDev
      ? "https://dev.bey.chat"
      : isStaging
        ? "https://staging.bey.chat"
        : "https://bey.chat"

  return `${baseUrl}/${agentId}`
}

export const decodeMessage = (message: string): string => {
  try {
    let processedMessage = message.replace(/^["'](.*)["']$/, "$1")

    // Check if the message is already decoded by attempting to encode it
    const encoded = JSON.stringify(processedMessage)
    const decoded = JSON.parse(encoded)

    if (processedMessage.includes("\\u")) {
      return decoded.replace(/\\u[\dA-F]{4}/gi, (match) =>
        String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16)),
      )
    }

    return processedMessage
  } catch (error) {
    return message.replace(/^["'](.*)["']$/, "$1")
  }
}

export const isFreePlan = (): boolean => {
  const user = useAppSelector((state) => state.app.user)
  return user?.subscriptionPlan === SubscriptionPlan.FREE
}

export const isCustomBrandingAllowed = (
  user: User,
  stripeProducts: StripeProducts,
): boolean => {
  if (!user?.subscriptionPlan || !stripeProducts) return false
  const userPlan = Object.values(stripeProducts).find(
    (product) =>
      product.name.toLowerCase() === user.subscriptionPlan?.toLowerCase(),
  )
  if (!userPlan) return false
  return userPlan.metadata?.allow_custom_branding === "true"
}

export const isDefaultAgent = (agent: Agent) => {
  return agent.creatorId === DefaultAgentCreatorId
}

export const isDefaultAvatar = (avatar: Avatar) => {
  return avatar.creatorId === DefaultAgentCreatorId
}

export const getConversationStatus = (
  conversation: Conversation,
): ConversationStatus => {
  if (conversation.leftAt === null) {
    return ConversationStatus.Live
  } else if (conversation.endedAt !== null) {
    return ConversationStatus.Completed
  } else {
    return ConversationStatus.UserLeft
  }
}

export const getConversationStatusProps = (
  conversation: Conversation,
): BadgeProps => {
  const status = getConversationStatus(conversation)

  switch (status) {
    case ConversationStatus.Live:
      return {
        icon: "Dot",
        color: BadgeColor.Green,
        text: "Live",
        iconWeight: "fill",
      }
    case ConversationStatus.Completed:
      return { icon: "Check", color: BadgeColor.Violet, text: "Completed" }
    case ConversationStatus.UserLeft:
      return { icon: "SignOut", color: BadgeColor.Red, text: "User Left" }
  }
}

export const getConversationName = (
  conversation: Conversation,
  agent: Agent,
) => {
  const date = dayjs(conversation.createdAt).format("DD.MM.YYYY HH:mm:ss")
  return `${agent?.name || "Agent"} with ${conversation?.userName || "User"} on ${date}`
}

export const getConversationIds = (
  conversationNames: string[],
  conversations: Conversation[],
  agent: Agent,
) => {
  return conversations
    .filter((conversation) =>
      conversationNames.includes(getConversationName(conversation, agent)),
    )
    .map((conversation) => conversation.id)
}

/**
 * Load HubSpot script dynamically only in production environment
 */
export const loadHubSpotScript = (): void => {
  if (!isProduction) {
    return
  }

  if (document.getElementById("hs-script-loader")) {
    return
  }

  const script = document.createElement("script")
  script.type = "text/javascript"
  script.id = "hs-script-loader"
  script.async = true
  script.defer = true
  script.src = "//js-eu1.hs-scripts.com/146556213.js"

  document.head.appendChild(script)
}

/**
 * Load Microsoft Clarity script dynamically only in production environment
 */
export const loadClarityScript = (): void => {
  if (!isProduction) {
    return
  }

  if (document.getElementById("clarity-script-loader")) {
    return
  }

  const script = document.createElement("script")
  script.type = "text/javascript"
  script.id = "clarity-script-loader"
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "t7ikvdmbdy");
  `

  document.head.appendChild(script)
}

/**
 * Load Google Analytics 4 script dynamically only in production environment
 */
export const loadGA4Script = (): void => {
  if (!isProduction) {
    return
  }

  if (document.getElementById("ga4-script-loader")) {
    return
  }

  const measurementId = "G-3S4PWHSDWL"

  const script = document.createElement("script")
  script.async = true
  script.id = "ga4-script-loader"
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`

  const inlineScript = document.createElement("script")
  inlineScript.id = "ga4-config-script"
  inlineScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}', {
      'linker': {
        'domains': ['beyondpresence.ai','bey.chat']
      }
    });
  `

  document.head.appendChild(script)
  document.head.appendChild(inlineScript)
}

export const loadGTMScript = (): void => {
  if (!isProduction) {
    return
  }

  if (document.getElementById("gtm-script-loader")) {
    return
  }

  const gtmId = "GTM-KQ3GSVNV"

  const script = document.createElement("script")
  script.id = "gtm-script-loader"
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `
  document.head.insertBefore(script, document.head.firstChild)

  const noscript = document.createElement("noscript")
  noscript.id = "gtm-noscript-loader"
  const iframe = document.createElement("iframe")
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
  iframe.height = "0"
  iframe.width = "0"
  iframe.style.display = "none"
  iframe.style.visibility = "hidden"
  noscript.appendChild(iframe)
  document.body.insertBefore(noscript, document.body.firstChild)
}

export const convertCsvToArray = (
  csv: string,
  splitter: string = ",",
): string[] => {
  if (!csv || typeof csv !== "string" || csv.trim() === "") return []
  return csv
    .split(splitter)
    .map((cell) => cell.trim())
    .filter((cell) => cell !== "")
}

export const convertArrayToCsv = (
  array: string[],
  joiner: string = ",",
): string => {
  if (!array || !Array.isArray(array) || array.length === 0) return ""
  return array.join(joiner)
}

export const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}
